import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCompare } from '../../context/CompareContext';
import { allTools } from '../../mocks/tools';
import { TOOL_LOGOS } from '../../mocks/tool-logos';

function MiniLogo({ slug, name }: { slug: string; name: string }) {
  const [err, setErr] = useState(false);
  const logoUrl = TOOL_LOGOS[slug] ?? '';
  const initials = (() => {
    const words = name.replace(/[^a-zA-Z0-9\s]/g, '').trim().split(/\s+/);
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
    return (words[0][0] + words[1][0]).toUpperCase();
  })();

  if (logoUrl && !err) {
    return (
      <img
        src={logoUrl}
        alt={name}
        width={24}
        height={24}
        className="w-6 h-6 object-contain"
        onError={() => setErr(true)}
      />
    );
  }
  return (
    <span className="text-[9px] font-bold text-gray-300 select-none">{initials}</span>
  );
}

export default function CompareBar() {
  const { compareSlugs, removeTool, clearAll, count } = useCompare();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => setVisible(count > 0), count > 0 ? 0 : 300);
    return () => clearTimeout(timeout);
  }, [count]);

  const tools = compareSlugs
    .map(slug => allTools.find(t => t.slug === slug))
    .filter(Boolean);

  const handleCompare = () => {
    if (count < 2) return;
    navigate(`/compare?tools=${compareSlugs.join(',')}`);
  };

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 z-50 transition-all duration-500 ease-out ${
        visible && count > 0
          ? 'translate-y-0 opacity-100'
          : 'translate-y-full opacity-0 pointer-events-none'
      }`}
    >
      <div className="bg-gray-950/98 backdrop-blur-xl border-t border-teal-500/30 shadow-[0_-8px_32px_rgba(20,184,166,0.12)]">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center gap-4">

            {/* Label */}
            <div className="flex items-center gap-2.5 shrink-0">
              <div className="w-8 h-8 flex items-center justify-center bg-teal-500/15 rounded-lg border border-teal-500/30">
                <i className="ri-bar-chart-grouped-line text-teal-400 text-sm"></i>
              </div>
              <div>
                <p className="text-white text-xs font-bold leading-tight">Compare</p>
                <p className="text-gray-400 text-xs leading-tight">{count} / 3 tools</p>
              </div>
            </div>

            {/* Divider */}
            <div className="w-px h-10 bg-gray-800 shrink-0"></div>

            {/* Tool slots */}
            <div className="flex items-center gap-2.5 flex-1 overflow-x-auto scrollbar-hide">
              {tools.map(tool => tool && (
                <div
                  key={tool.slug}
                  className="flex items-center gap-2 bg-gray-800/60 border border-gray-700/50 rounded-xl px-3 py-2 shrink-0 hover:border-teal-500/30 transition-colors group"
                >
                  <div className="w-7 h-7 rounded-lg overflow-hidden bg-[#1a2235] border border-gray-700/50 shrink-0 flex items-center justify-center">
                    <MiniLogo slug={tool.slug} name={tool.name} />
                  </div>
                  <span className="text-white text-xs font-semibold whitespace-nowrap max-w-[100px] truncate">
                    {tool.name}
                  </span>
                  <button
                    onClick={() => removeTool(tool.slug)}
                    className="w-4 h-4 flex items-center justify-center text-gray-500 hover:text-rose-400 transition-colors cursor-pointer shrink-0 ml-0.5"
                    title={`Remove ${tool.name}`}
                  >
                    <i className="ri-close-line text-xs"></i>
                  </button>
                </div>
              ))}

              {/* Empty slot placeholders */}
              {Array.from({ length: Math.max(0, 3 - count) }).map((_, i) => (
                <div
                  key={`empty-${i}`}
                  className="flex items-center gap-2 bg-gray-800/20 border border-gray-700/20 border-dashed rounded-xl px-3 py-2 shrink-0"
                >
                  <div className="w-7 h-7 rounded-lg bg-gray-800/50 flex items-center justify-center shrink-0 border border-gray-700/30 border-dashed">
                    <i className="ri-add-line text-gray-600 text-xs"></i>
                  </div>
                  <span className="text-gray-600 text-xs whitespace-nowrap">Add a tool</span>
                </div>
              ))}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={clearAll}
                className="px-3 py-2 text-gray-500 hover:text-rose-400 text-xs font-medium transition-colors cursor-pointer whitespace-nowrap rounded-lg hover:bg-rose-500/5"
              >
                <i className="ri-delete-bin-line mr-1"></i>
                Clear
              </button>
              <button
                onClick={handleCompare}
                disabled={count < 2}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  count >= 2
                    ? 'bg-teal-500 hover:bg-teal-400 text-white shadow-lg shadow-teal-500/30 hover:shadow-teal-400/40 hover:scale-105 active:scale-95'
                    : 'bg-gray-800 text-gray-500 cursor-not-allowed border border-gray-700/50'
                }`}
              >
                {count < 2 ? (
                  <>
                    <i className="ri-information-line text-xs"></i>
                    Add {2 - count} more
                  </>
                ) : (
                  <>
                    <i className="ri-bar-chart-grouped-line text-sm"></i>
                    Compare {count} Tools
                    <i className="ri-arrow-right-line text-sm"></i>
                  </>
                )}
              </button>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
